import { calculateHeaderGroupHeight } from "@theme/critical";
import { Component } from "@theme/component";
import { onDocumentLoaded, changeMetaThemeColor } from "@theme/utilities";

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ["headerRowTop"];

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    const heightValue = `${height}px`;
    // Set on both :root and body to ensure it's available everywhere
    document.documentElement.style.setProperty("--header-height", heightValue);
    document.body.style.setProperty("--header-height", heightValue);
  });

  /**
   * Handles scroll events on the window
   */
  #handleScroll = () => {
    // Scroll handler logic can be added here
    if (window.scrollY > 100) {
      this.classList.add("scrolled");
    } else {
      this.classList.remove("scrolled");
    }
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);

    // Initialize header state as always active
    this.dataset.stickyState = "active";

    // Check if page has alternative-page-header with image or normal hero
    const hasAlternativePageHeaderWithImage = document.querySelector(
      ".alternative-page-header-section .background-image-container",
    );
    const hasHero = document.querySelector(".hero");
    const firstSection = document.querySelector("main .shopify-section");
    // First section in main has a CTA child (variant 2 or 3)
    const hasCallToActionSection = firstSection?.querySelector(
      ".call-to-action--variant-2, .call-to-action--variant-3",
    );

    // Only add scroll listener if page has one of these sections
    if (
      hasAlternativePageHeaderWithImage ||
      hasHero ||
      hasCallToActionSection
    ) {
      // Add scroll listener
      window.addEventListener("scroll", this.#handleScroll, { passive: true });
    } else {
      // Add class to differentiate header when page doesn't have hero
      this.classList.add("header--no-hero");
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    // Reset on both :root and body
    document.documentElement.style.setProperty("--header-height", "0px");
    document.body.style.setProperty("--header-height", "0px");

    // Remove scroll listener
    window.removeEventListener("scroll", this.#handleScroll);
  }
}

if (!customElements.get("header-component")) {
  customElements.define("header-component", HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector("#header-component");
  const headerGroup = document.querySelector("#header-group");

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() =>
      calculateHeaderGroupHeight(header, headerGroup),
    );

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "childList") {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement))
              continue;
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
